import React, {useMemo, useState} from 'react';
import {Alert} from 'src/design/atoms/Alert';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {CourseAssignmentRuleResponse} from 'src/api/ProactApiClient';
import {EntityTypeEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {Spinner} from 'src/design/atoms/Spinner';
import {buildSourceEntityNameLookup} from 'src/app/assignment-rules/assignmentRuleHelpers';
import {useGetDepartmentSelectOptionsQuery} from 'src/redux/queries/department';
import {useGetProjectSelectOptionsQuery} from 'src/redux/queries/project';
import {useGetUserGroupSelectOptionsQuery} from 'src/redux/queries/userGroup';
import {useGetVendorSelectOptionsQuery} from 'src/redux/queries/vendor';
import {useGetCourseAssignmentRulesByCourseQuery} from 'src/redux/queries/courseAssignmentRule';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {AssignmentRuleCard} from './AssignmentRuleCard';
import {AssignmentRuleFormModal} from './AssignmentRuleFormModal';

export interface AssignmentRulesTabProps {
  courseDefinitionId: number;
}

/**
 * Assign tab replacement per Wave 3: audience is owned by
 * `CourseAssignmentRule` records with their own CRUD, decoupled entirely
 * from course-definition versioning/locking. See
 * docs/plans/training-admin-overhaul.md Wave 3.
 */
export const AssignmentRulesTab: React.FC<AssignmentRulesTabProps> = ({
  courseDefinitionId,
}) => {
  const trainingPerms = useModulePermissions(ModuleEnum.Training);

  const {
    data: rules,
    isLoading,
    isError,
  } = useGetCourseAssignmentRulesByCourseQuery(courseDefinitionId);

  const {data: departmentOptions} = useGetDepartmentSelectOptionsQuery({
    start: 0,
    length: 500,
  });
  const {data: groupOptions} = useGetUserGroupSelectOptionsQuery({
    start: 0,
    length: 500,
    active: true,
  });
  const {data: vendorOptions} = useGetVendorSelectOptionsQuery({
    start: 0,
    length: 500,
    active: true,
  });
  const {data: projectOptions} = useGetProjectSelectOptionsQuery({
    start: 0,
    length: 500,
  });

  const nameLookup = useMemo(
    () =>
      buildSourceEntityNameLookup({
        [EntityTypeEnum.Department]: departmentOptions?.data,
        [EntityTypeEnum.Group]: groupOptions?.data,
        [EntityTypeEnum.Vendor]: vendorOptions?.data,
        [EntityTypeEnum.Project]: projectOptions?.data,
      }),
    [
      departmentOptions?.data,
      groupOptions?.data,
      vendorOptions?.data,
      projectOptions?.data,
    ]
  );

  const [editingRule, setEditingRule] = useState<
    CourseAssignmentRuleResponse | undefined
  >(undefined);
  const [creating, setCreating] = useState(false);

  if (isLoading) {
    return <Spinner loadingText="Loading assignment rules..." />;
  }

  if (isError) {
    return (
      <Alert color="red">
        Failed to load assignment rules. Please try again later.
      </Alert>
    );
  }

  const rows = rules ?? [];

  return (
    <Stack gap="md">
      <Alert color="blue" py="xs">
        Assignment rules are live: new matching hires enroll automatically
        (within ~12 hours), and removing someone disenrolls them. Editing rules
        never versions or locks the course. After changing groups, departments,
        or supervisors, use Refresh Assignments on the Roster tab to apply the
        changes immediately.
      </Alert>

      {trainingPerms.create && (
        <Button
          size="sm"
          variant="outline"
          w="fit-content"
          onClick={() => setCreating(true)}>
          + Add Rule
        </Button>
      )}

      {rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          No assignment rules yet. This course has no audience; add a rule to
          start enrolling people.
        </Text>
      ) : (
        rows.map(rule => (
          <AssignmentRuleCard
            key={rule.id}
            rule={rule}
            nameLookup={nameLookup}
            canEdit={trainingPerms.update}
            onEdit={() => setEditingRule(rule)}
          />
        ))
      )}

      {creating && (
        <AssignmentRuleFormModal
          opened={creating}
          onClose={() => setCreating(false)}
          courseDefinitionId={courseDefinitionId}
        />
      )}

      {editingRule && (
        <AssignmentRuleFormModal
          opened={!!editingRule}
          onClose={() => setEditingRule(undefined)}
          courseDefinitionId={courseDefinitionId}
          rule={editingRule}
        />
      )}
    </Stack>
  );
};
